// WP2 owns this file after WP0 (architecture §7.3). Types verbatim; WP2 adds request schemas additively.
import { z } from 'zod';
import { MAILBOXES, type IntegrationKey, type IntegrationState, type Mailbox, type McpToolDef } from '../domain.ts';

export interface SetupStep { n: number; text: string; link?: { label: string; url: string } }
export interface IntegrationStatus { key: IntegrationKey; label: string; state: IntegrationState; detail: string | null;
  lastOkAt: string | null; lastError: string | null; setup: SetupStep[]; actions: Array<'testen' | 'verbinden' | 'trennen' | 'konfigurieren'>;
  testRoute: string | null }         // POST path of the "Testen" button (§12 table); null when the integration has no test
export interface McpServerView { id: string; slug: string; name: string; urlSource: 'env' | 'db'; urlHost: string | null;
  tokenSource: 'env' | 'db' | 'keiner'; enabled: boolean; seeded: boolean;
  tools: Array<McpToolDef & { exposure: 'connector' | 'twin' | 'nie'; geaendert: boolean /* defHash changed at the last refresh, set to aus */ }>;
  toolsFetchedAt: string | null; lastError: string | null }

// ---------------------------------------------------------------------------------------------------------------------
// WP2 request schemas (additive, §7.5).
// ---------------------------------------------------------------------------------------------------------------------

/** The address each Gmail slot may be connected to lives in domain.ts (`MAILBOX_ADDRESSES`, §12.2, CR-3). */
export type MailboxSlot = Mailbox;

export const GoogleStartQuery = z.object({ slot: z.enum(MAILBOXES) });
export const GoogleCallbackQuery = z.object({
  code: z.string().min(1).max(2000).optional(), state: z.string().min(1).max(200).optional(), error: z.string().max(200).optional(),
}).loose();

/** `^[a-z0-9_]{2,24}$` (§4.2 mcp_servers.slug). */
export const McpSlug = z.string().regex(/^[a-z0-9_]{2,24}$/, 'Kürzel: 2 bis 24 Zeichen, nur a-z, 0-9 und _');
const McpUrl = z.string().trim().min(8).max(500);
const McpToken = z.string().min(1).max(4000);

export const McpServerCreateBody = z.object({
  name: z.string().trim().min(1).max(60), slug: McpSlug, url: McpUrl, token: McpToken.optional(),
}).strict();
export type McpServerCreateBody = z.infer<typeof McpServerCreateBody>;

export const McpServerPatchBody = z.object({
  name: z.string().trim().min(1).max(60).optional(), url: McpUrl.optional(), token: McpToken.nullable().optional(), enabled: z.boolean().optional(),
}).strict();
export type McpServerPatchBody = z.infer<typeof McpServerPatchBody>;

/** PUT /api/integrations/ntfy (§12.5): null removes the configuration. */
export const NtfyBody = z.object({
  serverUrl: z.string().trim().min(8).max(300),
  topic: z.string().min(24, 'Das Thema braucht mindestens 24 Zeichen.').max(64).regex(/^[A-Za-z0-9_-]+$/, 'Thema: nur Buchstaben, Ziffern, - und _').optional(),
  token: z.string().min(1).max(500).nullable().optional(),
}).strict().nullable();
export type NtfyBody = z.infer<typeof NtfyBody>;
